import Promise from 'bluebird';
import _ from 'lodash';
import axios from 'axios';
import moment from 'moment';
import store from 'app-store';
import createUuid from 'uuid/v4';

import { CC_DOMAIN, SORTABLE_DATE_FORMAT, SORTABLE_TIME_FORMAT } from 'constants';
import { alerts } from 'modules/alerts/actions';
import { getDateRangeFilter, getRecurrenceFilter } from 'utils/filter-utils';
import Rest from 'services/data/rest';
import Task from 'services/data/task';

export default class Ticket extends Rest {
  static path = 'ticket';
  static defaultQuery = {
    load: {
      agreement: null,
      company: null,
      'agreement.rates': null,
      folder: null,
      closedBy: null,
      tasks: null,
      'tasks.checklist': null,
      'tasks.assignedTo': null
    }
  };

  static merge({ ticketToMerge, mergeInto, updateAgreement, updateTimeBillable }) {
    return Promise.resolve(axios.post(`/api/ticket/${ticketToMerge}/merge`, { mergeInto, updateAgreement, updateTimeBillable }))
      .tap(this.logRequest);
  }

  static taskTemplates() {
    return Promise.resolve(axios.get('/api/ticket/task-templates'))
      .tap(this.logRequest)
      .get('data')
      .catch(alerts.catchError);
  }

  static formatQuery(filters) {
    // Use omitBy isEmpty because empty filters will be set to empty string rather than undefined,
    // which will break filtering
    const {
      company,
      search,
      assignedTo,
      ticketStatus,
      ticketType,
      dueDate,
      team,
      taskRecurrence
    } = _.omitBy(filters, _.isEmpty);

    const loadingClosedArchived = _.intersection(ticketStatus, ['Closed', 'Archived']).length;

    const vals = {
      where: {
        company,
        search,
        'company.team': team,
        or: {
          'tasks.dueDate': getDateRangeFilter(dueDate),
          'tasks.startDate': getDateRangeFilter(dueDate)
        },
        'tasks.status': loadingClosedArchived ? { in: ['Complete', 'Active'] } : 'Active',
        'tasks.assignedTo.id': assignedTo ? { in: assignedTo } : undefined,
        status: _.isEmpty(ticketStatus) ? undefined : { in: ticketStatus },
        type: _.isEmpty(ticketType) ? undefined : { in: ticketType },
        'tasks.recurrence': getRecurrenceFilter(taskRecurrence)
      },
      load: _.defaults({
        tasks: {
          or: {
            dueDate: getDateRangeFilter(dueDate),
            startDate: getDateRangeFilter(dueDate)
          },
          status: loadingClosedArchived ? { in: ['Complete', 'Active'] } : 'Active',
          'assignedTo.id': assignedTo ? { in: assignedTo } : undefined,
          recurrence: getRecurrenceFilter(taskRecurrence)
        }
      }, this.defaultQuery.load)
    };

    return vals;
  }

  static urgency(ticket) {
    const isNormal = _.some(ticket.tasks, { priority: 'Normal' });
    const isUrgent = _.some(ticket.tasks, { priority: 'Urgent' });
    const isOverdue = _.some(ticket.tasks, task => Task.isOverdue(task));
    const isToday = _.some(ticket.tasks, task => Task.isToday(task));

    if (isUrgent || isOverdue) return 'high';
    if (isNormal && isToday) return 'normal';
    return 'low';
  }

  static sort(unsorted, prioritizeCritical) {
    _.forEach(unsorted, ticket => { ticket.tasks = Task.sort(ticket.tasks, prioritizeCritical); });

    const sorters = [
      ticket => moment(ticket.tasks[0].dueDate).format(SORTABLE_DATE_FORMAT),
      ticket => Task.urgencySort(ticket.tasks[0]),
      ticket => Ticket.typeSort(ticket),
      'company.name',
      'description',
      ticket => moment(ticket.tasks[0].startDate).format(SORTABLE_TIME_FORMAT),
      ticket => moment(ticket.tasks[0].createdAt).format(SORTABLE_TIME_FORMAT)
    ];

    const orders = ['asc', 'asc', 'asc', 'asc', 'asc', 'desc', 'desc'];

    if (prioritizeCritical) {
      sorters.unshift(ticket => Task.dateUrgencySort(ticket.tasks[0]));
      orders.unshift('asc');
    }

    return _.orderBy(unsorted, sorters, orders);
  }

  static typeSort(ticket) {
    return _.includes(['Help Desk', 'Project'], ticket.type) ? 1 : 2;
  }

  static getUrgency(ticket) {
    const task = ticket.tasks[0];
    const isToday = Task.isToday(task);
    const isOverdue = Task.isOverdue(task);
    const isUrgent = task.priority === 'Urgent' || task.priority === 'Critical';
    const isNormal = task.priority === 'Normal' || task.priority === 'Planned';

    if (isOverdue || (isToday && isUrgent)) return 'high';
    if (isToday && isNormal) return 'medium';
    return 'low';
  }

  static getGmailSearchLink(ticket) {
    const base = 'https://mail.google.com/mail/#search/';
    const id = encodeURIComponent(`ticket-${ticket.id}@${CC_DOMAIN}`);
    const plus = encodeURIComponent(`ticket+${ticket.id}@${CC_DOMAIN}`);
    const legacy = ticket.legacyId ? encodeURIComponent(`${ticket.legacyId}@${CC_DOMAIN}`) : '';
    const emails = _.filter(ticket.triageItems, { type: 'email' });
    const emailTerms = _.map(emails, email => encodeURIComponent(`email-${email.id}@${CC_DOMAIN}`));
    const emailPlusTerms = _.map(emails, email => encodeURIComponent(`email+${email.id}@${CC_DOMAIN}`));
    const terms = _.concat([], id, plus, legacy, emailTerms, emailPlusTerms);

    return base + _(terms).compact().join('+OR+');
  }

  static getMailtoLink(ticket, cc) {
    const encodedSubject = encodeURIComponent(`Re: ${ticket.description}`);
    const encodedCC = cc ? encodeURIComponent(cc) : encodeURIComponent(Ticket.getTicketCC(ticket));

    return `mailto:?subject=${encodedSubject}&cc=${encodedCC}`;
  }

  static getTeamEmails(ticket, currentUser, teams) {
    const teamId = _.get(ticket, 'company.team.id', ticket?.company?.team);

    if (!teamId) return '';

    const team = _.find(teams, { id: teamId }) || {};
    const teamMembers = _.reject(team.members, { id: currentUser.id });
    const teamMemberEmails = _.map(teamMembers, 'primaryEmail.email');
    return _.join(teamMemberEmails, ',');
  }

  static getTeam(ticket, teams) {
    const teamId = _.get(ticket, 'company.team.id', ticket?.company?.team);

    if (!teamId) return '';

    return _.find(teams, { id: teamId }) || {};
  }

  static getTicketCC(ticket) {
    return `ticket-${ticket.id}@${CC_DOMAIN}`;
  }

  static saveNewTask(ticket, task, updateTask) {
    const storeData = store.getState();
    const currentUser = storeData.auth.currentUser;
    const users = storeData.pageData.selectUsers;
    const userId = _.get(task, 'assignedTo[0].id', currentUser.id);
    const user = _.find(users, { id: userId });
    const uuid = createUuid();

    const newTask = {
      uuid,
      description: '',
      ticket: ticket.id,
      priority: 'Normal',
      status: 'Active',
      createdAt: moment().toDate(),
      dueDate: moment().startOf('day').toDate(),
      ...task,
      assignedTo: [{ id: userId }]
    };

    updateTask({
      ...newTask,
      assignedTo: [user]
    });

    return Ticket.update({
      id: ticket.id,
      tasks: [newTask]
    })
      .then(savedTicket => {
        const savedTask = _.find(savedTicket.tasks, { uuid });
        const taskData = _.pick(savedTask, ['uuid', 'id', 'ticket']);
        taskData.forceMerge = true;
        const ticketData = _.pick(savedTicket, ['openTaskCount']);
        ticketData.forceMerge = true;

        updateTask(taskData, ticketData);
      })
      .catch(alerts.catchError);
  }
}

